import { useState, type FormEvent } from 'react';
import { cn } from './cn';
import { Icon } from './Icon';

export interface SearchFieldProps {
  /** Product-specific wording. The only thing that should differ between sites. */
  placeholder?: string;
  defaultValue?: string;
  onSubmit?: (query: string) => void;
  onChange?: (query: string) => void;
  /** Shorter field for mobile drawers and dense bars. */
  compact?: boolean;
  /** Accessible label — describe what is being searched. */
  label?: string;
  autoFocus?: boolean;
  className?: string;
}

/**
 * One search field for both surfaces.
 *
 * Icon, height, focus state and type are fixed; only the placeholder changes.
 * `.men` keeps it persistently visible because commercial visitors arrive with
 * intent; `.app` opens it from an icon because discovery there is feed-led.
 * Same component, different placement — sisters, not twins.
 *
 * ── It is an underlined row, like every other field ────────────────────────
 *
 * It used to be the ONE boxed control in the estate: a filled, rounded rect
 * with a border on all four sides. The kit's own `Input` says why that was
 * wrong, in its own header — "a boxed input announces itself as a widget; an
 * underlined row lets the type carry the screen" — and search is nothing more
 * than an input. Two controls that are the same thing cannot disagree about
 * what a control is.
 *
 * The magnifier and the placeholder are what make it findable. The box was
 * never doing that job; it was just the only shape a search field is expected
 * to have.
 */
export const SearchField = ({
  placeholder = 'Search',
  defaultValue = '',
  onSubmit,
  onChange,
  compact,
  label = 'Search',
  autoFocus,
  className,
}: SearchFieldProps) => {
  const [value, setValue] = useState(defaultValue);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit?.(value.trim());
  };

  return (
    <form onSubmit={submit} className={cn('w-full', className)} role="search">
      <label className="relative block">
        <span className="sr-only">{label}</span>
        <Icon
          name="search"
          size={16}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
        />
        <input
          type="search"
          value={value}
          autoFocus={autoFocus}
          onChange={(e) => {
            setValue(e.target.value);
            onChange?.(e.target.value);
          }}
          placeholder={placeholder}
          className={cn(
            // An underlined row: no box, no fill, one rule. Same treatment as
            // Input, because it is the same control.
            'w-full rounded-none border-0 border-b border-white/32 bg-transparent',
            'pl-8 pr-0 text-[13px] text-white outline-none',
            'placeholder:text-white/32',
            'transition-[border-color] duration-base ease-mechanical',
            'hover:border-white/45',
            'focus:border-hottr-pink',
            // Kill the WebKit clear affordance — it renders a native blue X.
            '[&::-webkit-search-cancel-button]:appearance-none',
            // `min-h` rather than a fixed height, so the 2px-thick focus rule
            // is drawn on the last pixel instead of being cropped by the box.
            compact ? 'min-h-11' : 'min-h-11 md:min-h-12',
          )}
        />
      </label>
    </form>
  );
};
